'use client';

import { ThemedUserButton } from '@/components/auth/ThemedUserButton';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import type { HeaderAuthSectionProps } from '@/types/auth-types';
import { User } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import DirectAuthButtons from './DirectAuthButtons';
import LoadingState from './LoadingState';

function HeaderAuthSection({
  onItemClick,
  layout = 'inline',
}: HeaderAuthSectionProps) {
  const { isAuthenticated: isSignedIn, isLoading } = useAuth();
  const t = useTranslations('SidebarHeader');
  const [isAppMode, setIsAppMode] = useState(false);

  // Check if in "app mode" (history sidebar visible) by reading CSS variable set by MainContentArea
  // App mode = signed in user on research pages with sidebar visible (expanded or collapsed)
  useEffect(() => {
    const checkAppMode = () => {
      const sidebarOffset = getComputedStyle(
        document.documentElement
      ).getPropertyValue('--sidebar-left-offset');
      // App mode is active when sidebar offset > 0 (sidebar has its own account button)
      const inAppMode =
        Boolean(sidebarOffset) && Number.parseInt(sidebarOffset, 10) > 0;
      setIsAppMode(inAppMode);
    };

    checkAppMode();

    // Listen for changes (sidebar toggle)
    const observer = new MutationObserver(checkAppMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    });

    // Also check on resize (mobile/desktop switch)
    window.addEventListener('resize', checkAppMode);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkAppMode);
    };
  }, []);

  if (isLoading) return <LoadingState />;

  // In app mode, hide account button (sidebar has its own)
  if (isSignedIn && isAppMode) {
    return null;
  }

  if (isSignedIn) {
    return (
      <ThemedUserButton userProfileUrl='/user-profile'>
        <Button
          id='user-account-menu'
          variant='brandOutline'
          size='sm'
          className='group flex gap-2 px-4 py-2'
          onClick={() => {
            if (onItemClick) onItemClick();
          }}
        >
          <User className='h-4 w-4 transition-transform duration-300 group-hover:scale-110' />
          <span className='font-medium text-sm'>{t('account')}</span>
        </Button>
      </ThemedUserButton>
    );
  }

  return <DirectAuthButtons onItemClick={onItemClick} layout={layout} />;
}

export default HeaderAuthSection;
